/**
 * When a queued item gets another chance.
 *
 * <p>
 * All three offline queues held the same two triggers, copied between them: the window
 * `online` event, and one attempt at page load. Both describe the SAME situation — the
 * device regaining its network — because when they were written, being offline was the
 * only reason anything was ever queued.
 * </p>
 *
 * <p>
 * That stopped being true when the hold boundary widened to 502 and 504
 * (`ApiError.neverReachedTheServer`). A failing proxy never takes the browser offline,
 * so `navigator.onLine` stays true, no `online` event is ever dispatched, and neither
 * trigger fires. The item was held correctly and then never released: the photo sat
 * behind "1 photo waiting to be read" until someone reloaded the page, which at a trade
 * show booth means never. Holding is only better than failing if the hold ENDS, and
 * that made the wider boundary worse than the error message it replaced.
 * </p>
 *
 * <p>
 * So the release condition is now "the queue is not empty", not "the device came back",
 * and it is retried on a backoff until it drains. Backoff rather than a fixed interval
 * because the common cause is a backend that is down: a fixed 2s poll from every phone
 * at the booth is a small stampede aimed at something already struggling.
 * </p>
 */

/** Structural, not a base class — the managers already have these shapes. */
interface DrainableQueue {
  list(): Promise<unknown[]>;
  drain(): Promise<void>;
  /**
   * Optional: only the two queues with a UI have listeners. Without it, a queue is
   * armed at load and on `online` but NOT the moment something is enqueued while
   * online — which is the mapping queue, deliberately. It is best-effort by design
   * (a lost mapping degrades to "no suggestion", the same outcome the analyzer
   * already falls back to), and giving it a subscriber list nothing subscribes to
   * would be API written for this file rather than for the app.
   */
  subscribe?(fn: (items: unknown[]) => void): () => void;
}

const FIRST_RETRY_MS = 2_000;
const MAX_RETRY_MS = 30_000;

export function registerDrainTriggers(queue: DrainableQueue): void {
  if (typeof window === "undefined") return;

  let timer: number | null = null;
  let delay = FIRST_RETRY_MS;

  const schedule = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => void attempt(), delay);
  };

  const attempt = async (): Promise<void> => {
    timer = null;

    // Genuinely offline: stop burning retries. The `online` listener below is the
    // right trigger for that case and will restart this from the shortest delay.
    // @ui-check: this must stay reachable with no live API — every queue registered
    // here (punches, entries, mappings) depends on it never calling `queue.drain()`
    // while `navigator.onLine` is false (offline-punch.spec.ts's own AC9 gate).
    if (!navigator.onLine) {
      delay = FIRST_RETRY_MS;
      return;
    }

    // A THROW MUST NOT END THE RETRY LOOP.
    //
    // This used to be a bare `await queue.drain()` followed by the reschedule below. A
    // rejection therefore skipped `schedule()` entirely and, because `timer` is nulled at
    // the top of this function, left NOTHING armed.
    //
    // MEASURED in the running app, 2026-09-06, by instrumenting the outbox's IndexedDB
    // reads and faulting exactly one of them:
    //
    //     reads: [ { at_ms: 0, threw: true } ]     <- one read, nothing after it
    //     badge: "1 entry pending"                 <- still there fifteen seconds later
    //
    // No retry was ever scheduled again. The entry a farmer believes is saved sits on the
    // device, under a badge still promising it will sync.
    //
    // WHY AN EARLIER TEST SAID OTHERWISE, because it is the trap: driving this with
    // Playwright's `context.setOffline(false)` fires an `online` event, and the listener
    // below calls `kick()` on it. That handed the loop a second trigger the real failure
    // does not provide, so the bug looked absent. `navigator.onLine` stays TRUE on a
    // connected-but-useless network — a captive portal, a dead proxy, a bad cell edge —
    // which is exactly where a farmer is when this matters.
    //
    // It is reachable: `outbox.drain` is `try { … } finally { }` with no catch, so anything
    // from the IndexedDB layer propagates straight out, and `outboxStore` rejects on
    // "IndexedDB open failed", "open blocked", "read failed" and "write failed" — its own
    // header noting that Safari private browsing "has `indexedDB` present but can throw".
    try {
      await queue.drain();

      if ((await queue.list()).length === 0) {
        delay = FIRST_RETRY_MS;
        return;
      }
    } catch (err) {
      // Falls through to the reschedule rather than returning: the items are still queued,
      // and another attempt is precisely what is wanted.
      console.warn("A drain attempt failed; retrying with backoff.", err);
    }

    delay = Math.min(delay * 2, MAX_RETRY_MS);
    schedule();
  };

  /** Try now, from the shortest delay — the queue just changed, or the network did. */
  const kick = (): void => {
    delay = FIRST_RETRY_MS;
    void attempt();
  };

  // An item arriving while ONLINE — the 502 case — has no network transition to wait
  // for, so the queue's own change notification is what starts the clock. Only when
  // nothing is already scheduled, so a drain that empties the queue does not re-arm.
  queue.subscribe?.((items) => {
    if (items.length > 0 && timer === null) schedule();
  });

  window.addEventListener("online", kick);

  // The app loaded (or reloaded) with items a previous session left behind.
  if (navigator.onLine) kick();
}
